import { useState, type FormEvent } from 'react';

import type { BusinessOffer } from '../api/schemas';
import { formatMoneyCents } from '../lib/format';
import { parsePriceInput } from '../lib/money';
import { FormField } from './FormField';

/**
 * What's active on the cart right now: an ad-hoc Descuento the cashier typed,
 * or one derived from an Oferta the cashier picked. Exactly one at a time
 * (docs/CONCEPTS.md — Descuento is a single attribute cluster per Cobro);
 * the two pickers below share this one slot, which is what makes the XOR
 * automatic instead of something each picker has to enforce itself.
 */
export type DiscountSelection =
  | { source: 'adhoc'; type: 'percent' | 'fixed'; value: number }
  | { source: 'offer'; offerId: string; title: string; discount: BusinessOffer['discount'] };

function offerDiscountLabel(discount: BusinessOffer['discount']): string {
  return discount.type === 'percent' ? `${discount.percent}%` : formatMoneyCents(discount.amountCents);
}

const MIN_PERCENT = 1;
const MAX_PERCENT = 100;
const PERCENT_RANGE_MESSAGE = `Escribe un porcentaje entre ${MIN_PERCENT} y ${MAX_PERCENT}.`;

type ChargeDiscountPanelProps = {
  discount: DiscountSelection | undefined;
  offers: BusinessOffer[];
  onApplyAdhoc: (type: 'percent' | 'fixed', value: number) => void;
  onApplyOffer: (offer: BusinessOffer) => void;
  onClear: () => void;
};

/** "Descuento" ad-hoc entry and "Aplicar oferta" picker, XOR in the UI (see DiscountSelection). */
export function ChargeDiscountPanel({
  discount,
  offers,
  onApplyAdhoc,
  onApplyOffer,
  onClear,
}: ChargeDiscountPanelProps) {
  const [type, setType] = useState<'percent' | 'fixed'>('percent');
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | undefined>();
  const activeOffers = offers.filter((offer) => offer.isActive);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (type === 'percent') {
      const percent = Number(value);
      if (!Number.isFinite(percent) || percent < MIN_PERCENT || percent > MAX_PERCENT) {
        setError(PERCENT_RANGE_MESSAGE);
        return;
      }
      setError(undefined);
      onApplyAdhoc('percent', percent);
      return;
    }
    const price = parsePriceInput(value);
    if (!price.ok) {
      setError(price.message);
      return;
    }
    setError(undefined);
    onApplyAdhoc('fixed', price.cents);
  }

  function handleOfferChange(offerId: string) {
    if (offerId === '') {
      onClear();
      return;
    }
    const offer = activeOffers.find((candidate) => candidate.id === offerId);
    if (offer) onApplyOffer(offer);
  }

  return (
    <div className="pos-discount">
      <p className="pos-pane-label">Descuento</p>

      {discount ? (
        <div className="pos-discount-active">
          <span>
            {discount.source === 'offer'
              ? `Oferta: ${discount.title} (${offerDiscountLabel(discount.discount)})`
              : discount.type === 'percent'
                ? `Descuento: ${discount.value}%`
                : `Descuento: ${formatMoneyCents(discount.value)}`}
          </span>
          <button type="button" className="btn small ghost" onClick={onClear}>
            Quitar
          </button>
        </div>
      ) : null}

      <form className="pos-discount-form" onSubmit={handleSubmit}>
        <span className="seg mini" role="radiogroup" aria-label="Tipo de descuento">
          <button type="button" aria-pressed={type === 'percent'} onClick={() => setType('percent')}>
            %
          </button>
          <button type="button" aria-pressed={type === 'fixed'} onClick={() => setType('fixed')}>
            $
          </button>
        </span>
        <input
          id="discount-value"
          aria-label="Valor del descuento"
          inputMode="decimal"
          placeholder={type === 'percent' ? '10' : '5.00'}
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
        <button type="submit" className="btn small secondary">
          Aplicar
        </button>
      </form>
      {error ? <p className="field-error">{error}</p> : null}

      {activeOffers.length > 0 ? (
        <FormField label="Aplicar oferta" htmlFor="offer-picker">
          <select
            id="offer-picker"
            value={discount?.source === 'offer' ? discount.offerId : ''}
            onChange={(event) => handleOfferChange(event.target.value)}
          >
            <option value="">Ninguna</option>
            {activeOffers.map((offer) => (
              <option key={offer.id} value={offer.id}>
                {offer.title} · {offerDiscountLabel(offer.discount)}
              </option>
            ))}
          </select>
        </FormField>
      ) : null}
    </div>
  );
}
